import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { AgGridComponent } from '@modules/common/components/ag-grid/ag-grid.component';
import { AlertErrorComponent } from '@modules/common/components/alert-error/alert-error.component';
import { AlertSuccessComponent } from '@modules/common/components/alert-success/alert-success.component';
import { AlertWarningComponent } from '@modules/common/components/alert-warning/alert-warning.component';
import { AlertComponent } from '@modules/common/components/alert/alert.component';
import { AreYouSureDialogComponent } from '@modules/common/components/are-you-sure-dialog/are-you-sure-dialog';
import { BreadcrumbsComponent } from '@modules/common/components/breadcrumbs/breadcrumbs.component';
import { CopyrightComponent } from '@modules/common/components/copyright/copyright.component';
import { CountdownComponent } from '@modules/common/components/countdown/countdown.component';
import { DragHandleComponent } from '@modules/common/components/drag-handle/drag-handle.component';
import { ErrorDialogComponent } from '@modules/common/components/error-dialog/error-dialog.component';
import { FileFormControlComponent } from '@modules/common/components/file-form-control/file-form-control.component';
import { FileSearchDialogComponent } from '@modules/common/components/file-search-dialog/file-search-dialog.component';
import { FileSearchComponent } from '@modules/common/components/file-search/file-search.component';
import { LabeledListItemComponent } from '@modules/common/components/labeled-list-item/labeled-list-item.component';
import { LoadingOverlayComponent } from '@modules/common/components/loading-overlay/loading-overlay.component';
import { NoCaseSelectedOverlayComponent } from '@modules/common/components/no-case-selected-overlay/no-case-selected-overlay.component';
import { NotFoundComponent } from '@modules/common/components/not-found/not-found.component';
import { ObscuredInputComponent } from '@modules/common/components/obscured-input/obscured-input.component';
import { ParticipantBenefitsComparisonGridComponent } from '@modules/common/components/participant-benefits-comparison-grid/participant-benefits-comparison-grid.component';
import { PasswordRequirementsComponent } from '@modules/common/components/password-requirements/password-requirements.component';
import { ProcessExecutionStatusComponent } from '@modules/common/components/process-execution-status/process-execution-status.component';
import { RichEditorComponent } from '@modules/common/components/rich-editor/rich-editor.component';
import { SessionExpirationDialogComponent } from '@modules/common/components/session-expiration-dialog/session-expiration-dialog.component';
import { TableLoadingOverlayComponent } from '@modules/common/components/table-loading-overlay/table-loading-overlay.component';
import { TimePickerComponent } from '@modules/common/components/time-picker/time-picker.component';
import { ValueMaskingComponent } from '@modules/common/components/value-masking/value-masking.component';
import { DashedInputDirective } from '@modules/common/directives/dashed-input/dashed-input.directive';
import { ExpandableTableDetailRowDirective } from '@modules/common/directives/expandable-table-detail-row/expandable-table-detail-row.directive';
import { ScrollToDirective } from '@modules/common/directives/scrollTo/scroll-to.directive';
import { PhoneNumberPipe } from '@modules/common/pipes/phone-number.pipe';
import { TrustHtmlPipe } from '@modules/common/pipes/trust-html.pipe';
import { TooltipDirective } from '@modules/common/directives/tooltip/tooltip.directive';
import { CustomToolTipComponent } from '@modules/common/components/custom-tooltip/custom-tooltip.component';
import { ErrorIndicatorComponent } from './components/error-indicator/error-indicator.component';
import { FormBottomToolbarComponent } from './components/form-bottom-toolbar/form-bottom-toolbar.component';
import { PublishLinkComponent } from './components/publish-link/publish-link.component';
import { MatIconModule } from '@angular/material/icon';
import { ExpansionIndicatorComponent } from '@modules/common/components/expansion-indicator/expansion-indicator.component';
import { ErrorSummaryComponent } from './components/error-summary/error-summary.component';
import { RadioGroupYesNoComponent } from './components/radio-group-yes-no/radio-group-yes-no.component';
import { MultiSelectComponent } from './components/multi-select/multi-select.component';
import { MatRadioModule } from '@angular/material/radio';
import { NumberInputDirective } from '@modules/common/directives/number-input/number-input.directive';
import { WarningSummaryComponent } from '@modules/common/components/warning-summary/warning-summary.component';
import { MatPseudoCheckboxModule, MatRippleModule } from '@angular/material/core';
import { FormatEmptyDataPipe } from '@modules/common/pipes/format-empty-data.pipe';
import { AlertInfoComponent } from '@modules/common/components/alert-info/alert-info.component';
import { AnchorDisabledDirective } from '@modules/common/directives/anchor-disabled/anchor-disabled.directive';

@NgModule({
    declarations: [
        RichEditorComponent,
        DragHandleComponent,
        ExpandableTableDetailRowDirective,
        ScrollToDirective,
        AlertComponent,
        AlertErrorComponent,
        AlertInfoComponent,
        AlertSuccessComponent,
        AlertWarningComponent,
        LoadingOverlayComponent,
        TableLoadingOverlayComponent,
        CopyrightComponent,
        LabeledListItemComponent,
        BreadcrumbsComponent,
        TrustHtmlPipe,
        PasswordRequirementsComponent,
        ErrorDialogComponent,
        NoCaseSelectedOverlayComponent,
        SessionExpirationDialogComponent,
        ValueMaskingComponent,
        CountdownComponent,
        NotFoundComponent,
        FileSearchComponent,
        FileSearchDialogComponent,
        FileFormControlComponent,
        AreYouSureDialogComponent,
		TimePickerComponent,
		AgGridComponent,
		ObscuredInputComponent,
        ProcessExecutionStatusComponent,
        PhoneNumberPipe,
        DashedInputDirective,
        ParticipantBenefitsComparisonGridComponent,
        TooltipDirective,
        CustomToolTipComponent,
        ErrorIndicatorComponent,
        FormBottomToolbarComponent,
        PublishLinkComponent,
        ExpansionIndicatorComponent,
        ErrorSummaryComponent,
        RadioGroupYesNoComponent,
        MultiSelectComponent,
        NumberInputDirective,
        WarningSummaryComponent,
        FormatEmptyDataPipe,
        AnchorDisabledDirective,
    ],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSnackBarModule,
        MatSortModule,
        MatTableModule, 
        MatPaginatorModule,
        MatTooltipModule,
        MatInputModule,
        MatIconModule,
        MatRadioModule,
        MatPseudoCheckboxModule,
        MatRippleModule,
    ],
    exports: [
        RichEditorComponent,
        DragHandleComponent,
        ExpandableTableDetailRowDirective,
        ScrollToDirective,
        AlertErrorComponent,
        AlertInfoComponent,
        AlertSuccessComponent,
        AlertWarningComponent,
        CopyrightComponent,
        LoadingOverlayComponent,
        TableLoadingOverlayComponent,
        LabeledListItemComponent,
        BreadcrumbsComponent,
        TrustHtmlPipe,
        PasswordRequirementsComponent,
        NoCaseSelectedOverlayComponent,
        SessionExpirationDialogComponent,
        ValueMaskingComponent,
        CountdownComponent,
        NotFoundComponent,
        FileFormControlComponent,
		TimePickerComponent,
		AgGridComponent,
		ObscuredInputComponent,
        ProcessExecutionStatusComponent,
        PhoneNumberPipe,
        DashedInputDirective,
        ParticipantBenefitsComparisonGridComponent,
        TooltipDirective,
        CustomToolTipComponent,
        ErrorIndicatorComponent,
        FormBottomToolbarComponent,
        PublishLinkComponent,
        FormatEmptyDataPipe,
        ExpansionIndicatorComponent,
        ErrorSummaryComponent,
        RadioGroupYesNoComponent,
        MultiSelectComponent,
        NumberInputDirective,
        WarningSummaryComponent,
        AnchorDisabledDirective,
    ]
})
export class StingrayCommonModule {
}
